import axios from "axios";
import { getSession, signOut } from "next-auth/react";

import { trackDbApiAuthCall } from "./db-api-telemetry";
import { hasFirebaseUserId } from "./token-utils";

// Ensure the NextAuth Session augmentation from types.ts is included
import "./types";

export interface PerformTokenRefreshOptions {
  /**
   * Some consumers, such as Argo, can only use Firebase ID tokens. When set,
   * legacy db-api sessions are signed out instead of being refreshed.
   */
  requireFirebaseToken?: boolean;
}

/**
 * Resolves a fresh access token for the browser-side 401 → retry path.
 *
 * Mode-aware: legacy sessions (db-api-minted tokens, identified by the
 * firebase_user_id claim) refresh against db-api's /auth/refresh as before.
 * Firebase-only sessions never touch db-api — posting a Firebase refresh
 * token to /auth/refresh always fails and used to force-sign-out users.
 * Instead, the getSession() call re-runs the server-side JWT callback,
 * which refreshes expired Firebase ID tokens against
 * securetoken.googleapis.com and persists them in the session cookie.
 *
 * Signs the user out and rejects when no usable session exists or the
 * server-side callback marked the session errored (failed refresh, or a
 * legacy session expired by the db-api auth retirement).
 */
export async function performTokenRefresh(
  options: PerformTokenRefreshOptions = {},
): Promise<string> {
  const session = await getSession({ broadcast: false });
  const accessToken = session?.databaseApiAccessToken;

  if (!session || !accessToken) {
    await signOut({ redirect: true, callbackUrl: "/auth/login" });
    throw new Error("No session available");
  }

  if (session.error) {
    await signOut({ redirect: true, callbackUrl: "/auth/login" });
    throw new Error(session.error);
  }

  // Firebase-only session: the token was already refreshed server-side by
  // the getSession() call above, so just hand it to the retry.
  if (!hasFirebaseUserId(accessToken)) {
    return accessToken;
  }

  if (options.requireFirebaseToken) {
    await signOut({ redirect: true, callbackUrl: "/auth/login" });
    throw new Error("Firebase session required");
  }

  // Legacy db-api session: refresh against db-api's /auth/refresh.
  const refreshToken = session.databaseApiRefreshToken;
  if (!refreshToken) {
    await signOut({ redirect: true, callbackUrl: "/auth/login" });
    throw new Error("No refresh token available");
  }
  trackDbApiAuthCall(
    {
      apiKey: process.env.NEXT_PUBLIC_POSTHOG_KEY,
      host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    },
    {
      endpoint: "auth_refresh",
      caller: "browser_401_fallback",
      app: typeof window !== "undefined" ? window.location.hostname : undefined,
      userId: session.user?.id,
    },
  );
  const refreshResponse = await axios.post(
    `${process.env.NEXT_PUBLIC_CURRICULUM_API_URL}/auth/refresh`,
    { refresh_token: refreshToken },
  );
  const newAccessToken = refreshResponse.data.access_token;
  if (!newAccessToken) {
    throw new Error("No access token in refresh response");
  }
  return newAccessToken;
}
